// File: helpdesk-client-portal/src/services/api.js
// API Service for Client Portal
import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://agents.matix.one/api/v1',
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 30000
});

// SaaS: Extract org slug from subdomain
const getOrgSlug = () => {
    const hostname = window.location.hostname;
    const match = hostname.match(/^([a-z0-9]+)-helpdesk.matix.one$/);
    return match ? match[1] : null;
};

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('helpdesk_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // SaaS: Send org slug header
        const slug = getOrgSlug();
        if (slug) {
            config.headers["X-Org-Slug"] = slug;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            localStorage.removeItem('helpdesk_token');
            localStorage.removeItem('helpdesk_client');
            
            // Redirect to login if not already there
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// =====================================================
// Helpdesk API Functions
// =====================================================
export const helpdeskAPI = {
    // Auth
    login: (email, password) => api.post('/helpdesk/client/login', { email, password }),
    register: (data) => api.post('/helpdesk/client/register', data),
    verifyToken: () => api.get('/helpdesk/client/verify'),
    updateProfile: (data) => api.put('/helpdesk/client/profile', data),
    changePassword: (data) => api.put('/helpdesk/client/password', data),

    // Categories & Departments
    getCategories: () => api.get('/helpdesk/categories'),
    getDepartments: () => api.get('/helpdesk/departments'),
    getSubDepartments: (deptId) => api.get(`/helpdesk/departments/${deptId}/sub-departments`),

    // Companies
    getCompanies: () => api.get('/helpdesk/companies'),
    addCompany: (companyName) => api.post('/helpdesk/companies', { company_name: companyName }),
    updateCompany: (companyId, companyName) => api.put('/helpdesk/companies/' + companyId, { company_name: companyName }),

    // Tickets
    getTickets: (params) => api.get('/helpdesk/tickets', { params }),
    getTicket: (id) => api.get('/helpdesk/tickets/' + id),
    createTicket: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
            if (key === 'attachments' && Array.isArray(data[key])) {
                data[key].forEach(file => {
                    formData.append('attachments', file);
                });
            } else if (data[key] !== null && data[key] !== undefined) {
                formData.append(key, data[key]);
            }
        });
        return api.post('/helpdesk/tickets', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    // Screen recordings: the backend reserves one in Matix Shot; the browser uploads it directly.
    getRecordingConfig: () => api.get('/helpdesk/recordings/config'),
    reserveRecording: (data) => api.post('/helpdesk/recordings', data),
    addReply: (ticketId, data) => {
        const formData = new FormData();
        formData.append('message', data.message);
        if (data.recording_id) formData.append('recording_id', data.recording_id);
        if (data.attachments && Array.isArray(data.attachments)) {
            data.attachments.forEach(file => {
                formData.append('attachments', file);
            });
        }
        return api.post('/helpdesk/tickets/' + ticketId + '/reply', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    // Close and Reopen Tickets
    closeTicket: (ticketId) => api.put('/helpdesk/tickets/' + ticketId + '/close'),
    reopenTicket: (ticketId) => api.put('/helpdesk/tickets/' + ticketId + '/reopen'),

    // Stats
    getStats: () => api.get('/helpdesk/stats'),

    // Attachments
    downloadAttachment: (id) => api.get('/helpdesk/attachments/' + id + '/download', {
        responseType: 'blob'
    })
};

export default api;